import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, ExternalLink, Loader2, Mail, RefreshCw, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { invokeEdgeFunction } from '../../lib/invokeEdgeFunction';
import { supabase } from '../../lib/supabase';
import {
  defaultAcceptMessage,
  defaultAcceptSubject,
  fetchCtoWaitlist,
  formatWaitDuration,
  linkedinHref,
  MCP_SETUP_URL,
  saveWaitlistStatus,
  sortWaitlist,
  isLiveActivated,
  queueAccessStatus,
  waitlistStep,
  type CtoWaitlistRow,
  type WaitlistStatus,
} from '../../lib/ctoWaitlist';
import { SUBSCRIBER_ACCESS_STATUSES } from '../../lib/subscriberAccessStatus';
import { formatDate } from '../../lib/ctoMembers';

type AccessFilter = 'all' | WaitlistStatus;
type ActivationFilter = 'all' | 'pending' | 'activated';
type FilterMode = 'waitlist' | 'activation';

/**
 * Who asked for My AI CTO and where they are in the funnel.
 *
 * Waitlist status (waitlisted / invited) and activation are separate facts.
 * Invited people stay invited after they accept terms — the activation column
 * is how you see who is still pending.
 */
export default function CtoWaitlistView() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<CtoWaitlistRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [accessFilter, setAccessFilter] = useState<AccessFilter>('all');
  const [activationFilter, setActivationFilter] = useState<ActivationFilter>('all');
  const [filterMode, setFilterMode] = useState<FilterMode>('waitlist');
  const [openId, setOpenId] = useState<string | null>(null);
  const [acceptTarget, setAcceptTarget] = useState<CtoWaitlistRow | null>(null);
  const reloadTimer = useRef<number | null>(null);

  const load = useCallback(async (opts?: { silent?: boolean }) => {
    if (!opts?.silent) setLoading(true);
    try {
      setRows(sortWaitlist(await fetchCtoWaitlist()));
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Failed to load waitlist');
      if (!opts?.silent) setRows([]);
    } finally {
      if (!opts?.silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const scheduleReload = () => {
      if (reloadTimer.current) window.clearTimeout(reloadTimer.current);
      reloadTimer.current = window.setTimeout(() => {
        void load({ silent: true });
      }, 400);
    };

    const channel = supabase
      .channel('cto-waitlist-live')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'subscriber_mcp_access' },
        scheduleReload,
      )
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'subscriber_mcp_activations' },
        scheduleReload,
      )
      .subscribe();

    const onVisible = () => {
      if (document.visibilityState === 'visible') scheduleReload();
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      if (reloadTimer.current) window.clearTimeout(reloadTimer.current);
      document.removeEventListener('visibilitychange', onVisible);
      void supabase.removeChannel(channel);
    };
  }, [load]);

  const waitlisted = useMemo(
    () => rows.filter((r) => queueAccessStatus(r) === 'waitlisted'),
    [rows],
  );
  const invited = useMemo(
    () => rows.filter((r) => queueAccessStatus(r) === 'invited'),
    [rows],
  );
  const pending = useMemo(
    () => rows.filter((r) => waitlistStep(r) === 'pending'),
    [rows],
  );
  const activated = useMemo(
    () => rows.filter((r) => waitlistStep(r) === 'activated'),
    [rows],
  );

  const usingActivation = filterMode === 'activation';

  function onWaitlistFilter(next: AccessFilter) {
    setFilterMode('waitlist');
    setAccessFilter(next);
    setActivationFilter('all');
  }

  function onActivationFilter(next: ActivationFilter) {
    setFilterMode('activation');
    setActivationFilter(next);
    setAccessFilter('all');
  }

  const visible = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return rows.filter((r) => {
      if (usingActivation) {
        if (activationFilter !== 'all' && waitlistStep(r) !== activationFilter) return false;
      } else if (accessFilter === 'all') {
        if (isLiveActivated(r)) return false;
      } else if (queueAccessStatus(r) !== accessFilter) {
        return false;
      }
      if (!q) return true;
      return (
        r.email.toLowerCase().includes(q) ||
        (r.name ?? '').toLowerCase().includes(q) ||
        (r.stage ?? '').toLowerCase().includes(q) ||
        (r.building ?? '').toLowerCase().includes(q) ||
        (r.linkedin ?? '').toLowerCase().includes(q)
      );
    });
  }, [accessFilter, activationFilter, rows, searchQuery, usingActivation]);

  return (
    <div className="max-w-7xl mx-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Clock className="h-7 w-7 text-amber-500" />
            Waitlist
          </h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
            Waitlist is the queue. Once they activate they leave Invited — that step is
            done — and show as Activated / Members.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          disabled={loading}
          title="Refresh"
          aria-label="Refresh"
          className="inline-flex items-center justify-center p-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {!loading ? (
        <div className="grid grid-cols-3 gap-3 mb-5 sm:max-w-xl">
          <Stat label="Waitlisted" value={waitlisted.length} tone="amber" />
          <Stat label="Invited" value={invited.length} />
          <Stat label="Activated" value={activated.length} />
        </div>
      ) : null}

      <div className="flex flex-col gap-3 mb-4 lg:flex-row lg:items-center">
        <div className="flex flex-wrap gap-3">
          <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 p-0.5 self-start">
            <FilterBtn
              active={!usingActivation && accessFilter === 'all'}
              onClick={() => onWaitlistFilter('all')}
              label="All waitlist"
            />
            <FilterBtn
              active={!usingActivation && accessFilter === 'waitlisted'}
              onClick={() => onWaitlistFilter('waitlisted')}
              label={`Waitlisted (${waitlisted.length})`}
            />
            <FilterBtn
              active={!usingActivation && accessFilter === 'invited'}
              onClick={() => onWaitlistFilter('invited')}
              label={`Invited (${invited.length})`}
            />
          </div>
          <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 p-0.5 self-start">
            <FilterBtn
              active={usingActivation && activationFilter === 'all'}
              onClick={() => onActivationFilter('all')}
              label="All activation"
            />
            <FilterBtn
              active={usingActivation && activationFilter === 'pending'}
              onClick={() => onActivationFilter('pending')}
              label={`Pending (${pending.length})`}
            />
            <FilterBtn
              active={usingActivation && activationFilter === 'activated'}
              onClick={() => onActivationFilter('activated')}
              label={`Activated (${activated.length})`}
            />
          </div>
        </div>
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="search"
            placeholder="Search name, email, stage, or what they are building…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm"
          />
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title="Nobody is on the waitlist yet"
          body="Applications land here from members.stephansmith.me/setup. There is nothing to review until someone applies."
        />
      ) : visible.length === 0 ? (
        <EmptyState
          title={emptyCopy(filterMode, accessFilter, activationFilter, searchQuery).title}
          body={emptyCopy(filterMode, accessFilter, activationFilter, searchQuery).body}
        />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900/50 text-left text-xs uppercase text-gray-500">
              <tr>
                <th className="px-4 py-3">Person</th>
                <th className="px-4 py-3">On list</th>
                <th className="px-4 py-3">Waitlist</th>
                <th className="px-4 py-3">Activation</th>
                <th className="px-4 py-3">Stage</th>
                <th className="px-4 py-3">What they are building</th>
                <th className="px-4 py-3">LinkedIn</th>
                <th className="px-4 py-3 text-right"> </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {visible.map((row) => (
                <WaitlistRow
                  key={row.subscriberId}
                  row={row}
                  open={openId === row.subscriberId}
                  onToggle={() =>
                    setOpenId((id) => (id === row.subscriberId ? null : row.subscriberId))
                  }
                  onOpenCrm={() => navigate(`/subscribers/${row.subscriberId}`)}
                  onAccept={() => setAcceptTarget(row)}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      {acceptTarget ? (
        <AcceptInviteModal
          row={acceptTarget}
          onClose={() => setAcceptTarget(null)}
          onSent={async () => {
            setAcceptTarget(null);
            await load({ silent: true });
          }}
        />
      ) : null}
    </div>
  );
}

function WaitlistRow({
  row,
  open,
  onToggle,
  onOpenCrm,
  onAccept,
}: {
  row: CtoWaitlistRow;
  open: boolean;
  onToggle: () => void;
  onOpenCrm: () => void;
  onAccept: () => void;
}) {
  const href = linkedinHref(row.linkedin);
  const acceptLabel = 'Edit status';
  const showInvite = !isLiveActivated(row);

  return (
    <>
      <tr
        onClick={onToggle}
        className="bg-white dark:bg-gray-800 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40"
      >
        <td className="px-4 py-3">
          <div className="font-medium text-gray-900 dark:text-gray-100">
            {row.name ?? row.email}
          </div>
          {row.name ? (
            <div className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">{row.email}</div>
          ) : null}
        </td>
        <td className="px-4 py-3 text-gray-600 dark:text-gray-400 whitespace-nowrap">
          <div>{formatWaitDuration(row.waitlistedAt)}</div>
          <div className="mt-0.5 text-xs text-gray-500">{formatDate(row.waitlistedAt)}</div>
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <WaitlistBadge status={queueAccessStatus(row)} />
        </td>
        <td className="px-4 py-3 whitespace-nowrap">
          <ActivationBadge row={row} />
        </td>
        <td className="px-4 py-3 text-gray-900 dark:text-gray-100">
          {row.stage ?? <span className="text-gray-400">—</span>}
        </td>
        <td className="px-4 py-3 text-gray-700 dark:text-gray-300 max-w-md">
          {row.building ? (
            <p className="line-clamp-2">{row.building}</p>
          ) : (
            <span className="text-gray-400">No answer</span>
          )}
        </td>
        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
          {href ? (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sky-700 dark:text-sky-300 hover:underline"
            >
              Open
              <ExternalLink className="h-3.5 w-3.5" />
            </a>
          ) : (
            <span className="text-gray-400">—</span>
          )}
        </td>
        <td className="px-4 py-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
          {showInvite ? (
            <button
              type="button"
              onClick={onAccept}
              className="inline-flex items-center gap-1.5 rounded-lg bg-neutral-900 px-3 py-1.5 text-xs font-semibold text-white hover:bg-neutral-800 dark:bg-white dark:text-neutral-900 dark:hover:bg-gray-200"
            >
              <Mail className="h-3.5 w-3.5" />
              {acceptLabel}
            </button>
          ) : null}
        </td>
      </tr>
      {open ? (
        <tr className="bg-gray-50 dark:bg-gray-900/40">
          <td colSpan={8} className="px-4 py-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between lg:gap-8">
              <p className="min-w-0 flex-1 text-sm text-gray-800 dark:text-gray-200 whitespace-pre-wrap">
                {row.building || 'No answer'}
              </p>
              <div className="shrink-0 space-y-2 lg:w-[22rem] lg:text-right">
                <div className="flex flex-wrap gap-x-6 gap-y-2 lg:justify-end">
                  <DateChip label="Applied" at={row.waitlistedAt} />
                  <DateChip label="Invited" at={row.invitedAt} />
                  <DateChip
                    label="Activated"
                    at={row.activatedAt && !row.revokedAt ? row.activatedAt : null}
                    empty={
                      row.accessStatus === 'invited' ? 'Pending' : '—'
                    }
                  />
                </div>
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm lg:justify-end">
                  {href ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-sky-700 dark:text-sky-300 hover:underline"
                    >
                      LinkedIn
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  ) : (
                    <span className="text-gray-400">No LinkedIn</span>
                  )}
                  <button
                    type="button"
                    onClick={onOpenCrm}
                    className="font-medium text-gray-600 dark:text-gray-300 underline"
                  >
                    Open connection
                  </button>
                </div>
              </div>
            </div>
          </td>
        </tr>
      ) : null}
    </>
  );
}

function AcceptInviteModal({
  row,
  onClose,
  onSent,
}: {
  row: CtoWaitlistRow;
  onClose: () => void;
  onSent: () => void | Promise<void>;
}) {
  const [status, setStatus] = useState<'not_applied' | WaitlistStatus>(row.accessStatus);
  const [sendEmail, setSendEmail] = useState(row.accessStatus === 'invited');
  const [subject, setSubject] = useState(defaultAcceptSubject);
  const [message, setMessage] = useState(() => defaultAcceptMessage(row.name, row.email));
  const [busy, setBusy] = useState(false);

  const canEmail = status === 'invited';
  const emailOn = sendEmail && canEmail;

  useEffect(() => {
    setSendEmail(canEmail);
  }, [canEmail]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  async function handleSave() {
    if (emailOn && !message.trim()) {
      toast.error('Write a short note before sending.');
      return;
    }

    setBusy(true);
    try {
      if (emailOn) {
        await invokeEdgeFunction<{ success?: boolean; error?: string }>('llc-send-mcp-invite', {
          subscriber_id: row.subscriberId,
          subject: subject.trim() || defaultAcceptSubject(),
          personal_message: message.trim(),
        });
        toast.success(`Invited ${row.email} and sent the get-started email`);
      } else {
        await saveWaitlistStatus(row.subscriberId, status, {
          waitlistedAt: row.waitlistedAt,
          invitedAt: row.invitedAt,
        });
        const label =
          SUBSCRIBER_ACCESS_STATUSES.find((s) => s.value === status)?.label ?? status;
        toast.success(`Waitlist status set to ${label}`);
      }
      await onSent();
    } catch (e: unknown) {
      toast.error(e instanceof Error ? e.message : 'Could not update waitlist');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div
        role="dialog"
        aria-labelledby="mcp-accept-title"
        className="w-full max-w-lg rounded-xl bg-white dark:bg-gray-800 shadow-xl border border-gray-200 dark:border-gray-700"
      >
        <div className="flex items-start justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 id="mcp-accept-title" className="text-lg font-semibold text-gray-900 dark:text-white">
              Edit Waitlist Status
            </h2>
            {row.name ? (
              <p className="mt-1 text-sm text-gray-900 dark:text-gray-100">{row.name}</p>
            ) : null}
            <p className={`${row.name ? 'mt-0.5' : 'mt-1'} text-sm text-gray-600 dark:text-gray-400`}>
              {row.email}
            </p>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
              Waitlisted {formatWaitDuration(row.waitlistedAt)}
              {row.waitlistedAt ? ` · ${formatDate(row.waitlistedAt)}` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="p-1 rounded text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-3">
          {row.building ? (
            <div className="rounded-lg bg-gray-50 dark:bg-gray-900/50 px-3 py-2 text-sm text-gray-700 dark:text-gray-300">
              <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">
                Their answer
              </div>
              {row.stage ? <p className="mb-1 font-medium">{row.stage}</p> : null}
              <p className="whitespace-pre-wrap">{row.building}</p>
            </div>
          ) : null}

          <div>
            <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
              Waitlist status
            </span>
            <div
              className="mt-1.5 grid grid-cols-1 rounded-lg border border-gray-300 dark:border-gray-600 p-0.5 sm:grid-cols-3"
              role="group"
              aria-label="Waitlist status"
            >
              {SUBSCRIBER_ACCESS_STATUSES.map((option) => {
                const active = status === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    disabled={busy}
                    aria-pressed={active}
                    onClick={() => setStatus(option.value as 'not_applied' | WaitlistStatus)}
                    className={`px-2 py-2 text-xs font-semibold rounded-md ${
                      active
                        ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
                        : 'text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'
                    }`}
                  >
                    {option.label}
                  </button>
                );
              })}
            </div>
          </div>

          {canEmail ? (
            <p className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2.5 text-sm leading-relaxed text-sky-950 dark:border-sky-900 dark:bg-sky-950/40 dark:text-sky-100">
              Their members account will update right away. They’ll get an email that
              takes them to{' '}
              <a
                href={MCP_SETUP_URL}
                target="_blank"
                rel="noreferrer"
                className="font-semibold underline underline-offset-2"
              >
                setup
              </a>{' '}
              and tells them how to get started.
            </p>
          ) : null}

          {canEmail ? (
          <label className="flex items-start gap-2.5 rounded-lg border border-gray-300 dark:border-gray-600 px-3 py-2.5">
            <input
              type="checkbox"
              checked={emailOn}
              disabled={busy}
              onChange={(e) => setSendEmail(e.target.checked)}
              className="mt-0.5 h-4 w-4 accent-neutral-900"
            />
            <span>
              <span className="block text-sm font-medium text-gray-900 dark:text-gray-100">
                Send get-started email
              </span>
              <span className="block text-xs text-gray-500 dark:text-gray-400">
                Opens {MCP_SETUP_URL} so they can finish setup.
              </span>
            </span>
          </label>
          ) : null}

          {emailOn ? (
            <>
              <label className="block">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">
                  Subject
                </span>
                <input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  disabled={busy}
                  className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Note</span>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  disabled={busy}
                  rows={7}
                  className="mt-1 w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-3 py-2 text-sm"
                />
              </label>
            </>
          ) : null}
        </div>

        <div className="flex justify-end gap-2 px-5 py-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void handleSave()}
            disabled={busy}
            className="inline-flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-lg bg-neutral-900 text-white hover:bg-neutral-800 disabled:opacity-50"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : emailOn ? <Mail className="h-4 w-4" /> : null}
            {busy ? 'Saving…' : emailOn ? 'Save & send email' : 'Save status'}
          </button>
        </div>
      </div>
    </div>
  );
}

function emptyCopy(
  mode: FilterMode,
  access: AccessFilter,
  activation: ActivationFilter,
  search: string,
): { title: string; body: string } {
  if (search.trim()) {
    return {
      title: 'No one matches that search',
      body: 'Try another name, email, stage, or what they said they are building.',
    };
  }
  if (mode === 'activation') {
    if (activation === 'pending') {
      return {
        title: 'No one is pending activation',
        body: 'There are no invited people waiting to accept terms in this state. Invite someone from the waitlist, or check Activated if they already got on.',
      };
    }
    if (activation === 'activated') {
      return {
        title: 'No one has activated yet',
        body: 'Nobody in this state has accepted terms and started using the product. They will show here after they finish setup.',
      };
    }
    return {
      title: 'No activation records',
      body: 'There is nobody to show for activation right now.',
    };
  }
  if (access === 'waitlisted') {
    return {
      title: 'No one is waitlisted',
      body: 'There are no people waiting for a review in this state. New applications from setup will land here.',
    };
  }
  if (access === 'invited') {
    return {
      title: 'No one is invited',
      body: 'There are no open invites in this state. People you invite will show here until they activate.',
    };
  }
  return {
    title: 'No one is in the waitlist queue',
    body: 'The open queue is empty in this state — nobody waitlisted or invited. Activated people are under Members, or All activation.',
  };
}

function EmptyState({ title, body }: { title: string; body: string }) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-8 py-16 text-center">
      <p className="text-xl font-semibold text-gray-900 dark:text-white">{title}</p>
      <p className="mx-auto mt-3 max-w-xl text-base leading-relaxed text-gray-600 dark:text-gray-400">
        {body}
      </p>
    </div>
  );
}

function FilterBtn({
  active,
  onClick,
  label,
  disabled,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`px-3 py-1.5 text-xs font-medium rounded-md disabled:cursor-not-allowed ${
        active
          ? 'bg-gray-900 text-white dark:bg-white dark:text-gray-900'
          : 'text-gray-600 dark:text-gray-300'
      }`}
    >
      {label}
    </button>
  );
}

function WaitlistBadge({ status }: { status: WaitlistStatus | 'activated' }) {
  if (status === 'activated') {
    return (
      <span className="inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
        Activated
      </span>
    );
  }
  const invited = status === 'invited';
  return (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${
        invited
          ? 'bg-neutral-900 text-white dark:bg-white dark:text-neutral-900'
          : 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200'
      }`}
    >
      {invited ? 'Invited' : 'Waitlisted'}
    </span>
  );
}

function ActivationBadge({ row }: { row: CtoWaitlistRow }) {
  const step = waitlistStep(row);
  if (step === 'activated') {
    return (
      <div>
        <span className="inline-flex rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200">
          On
        </span>
        <div className="mt-0.5 text-xs text-gray-500">{formatDate(row.activatedAt)}</div>
      </div>
    );
  }
  if (step === 'pending') {
    return (
      <span className="inline-flex rounded-full bg-sky-100 px-2 py-0.5 text-xs font-semibold text-sky-800 dark:bg-sky-900/40 dark:text-sky-200">
        Pending
      </span>
    );
  }
  return <span className="text-gray-400">—</span>;
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'amber' | 'sky';
}) {
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-3">
      <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </div>
      <div
        className={`mt-1 text-xl font-semibold tabular-nums ${
          tone === 'amber'
            ? 'text-amber-600 dark:text-amber-400'
            : tone === 'sky'
              ? 'text-sky-600 dark:text-sky-400'
              : 'text-gray-900 dark:text-gray-100'
        }`}
      >
        {value.toLocaleString()}
      </div>
    </div>
  );
}

function DateChip({
  label,
  at,
  empty = '—',
}: {
  label: string;
  at: string | null;
  empty?: string;
}) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-gray-400">{label}</div>
      {at ? (
        <>
          <div className="text-sm text-gray-800 dark:text-gray-200">{formatWaitDuration(at)}</div>
          <div className="text-xs text-gray-500">{formatDate(at)}</div>
        </>
      ) : (
        <div className="text-sm text-gray-400">{empty}</div>
      )}
    </div>
  );
}
